<template>
  <div class="nav-top border-b border-neutral-300">
    <div class="flex justify-between py-4 px-5 bg-white">
      <div class="w-60 flex gap-4 items-center">
        <div class="flex items-center gap-12">
          <div class="cursor-pointer" @click="toggleMenu()">
            <Hamburger />
          </div>
        </div>
      </div>
      <div class="menu flex items-center gap-3" v-if="!isMobile">
        <!-- Notification Icon with Dropdown -->
        <div class="relative cursor-pointer mr-5" @click="toggleNotifications()">
          <Bell />
          <span
            v-if="unreadNotifications && unreadNotifications > 0"
            class="absolute bg-red-500 text-white font-bold rounded-full flex items-center justify-center"
            :class="{
              // Posisi dan ukuran untuk angka 1-9
              'h-5 w-5 text-xs -top-2 -right-1': unreadNotifications <= 9,
              // Posisi dan ukuran untuk angka 10+
              'h-6 w-7 text-xs -top-3 -right-3': unreadNotifications > 9
            }"
          >
            <template v-if="unreadNotifications > 10">
              <span class="text-xs leading-none">10</span>
              <span class="text-[0.5rem] font-extrabold leading-none relative -top-[0.2em]">+</span>
            </template>
            <template v-else>
              <span class="text-xs leading-none">{{ unreadNotifications }}</span>
            </template>
          </span>

          <div
            v-if="showNotifications"
            class="absolute bg-white shadow-lg border border-gray-200 rounded-lg z-50 w-80 sm:w-96 right-0 mt-3"
            :class="{
              'opacity-100 visible translate-y-0': showNotifications,
              'opacity-0 invisible translate-y-2': !showNotifications
            }"
            style="transition: opacity 0.3s ease, transform 0.3s ease, visibility 0s 0.3s;"
          >
            <div class="relative z-10 rounded bg-white text-black w-full">
              <div class="p-3 border-b border-neutral-200 flex justify-around sticky top-0 bg-white z-20 shadow-sm">
                <button
                  @click.stop="changeFilter('ALL')"
                  :class="{
                    // Perbaikan 1: Menggunakan text-teal-700
                    'font-bold text-teal-700 border-b-2 border-teal-700': statusFilter === 'ALL',
                    'text-neutral-500 hover:text-neutral-700': statusFilter !== 'ALL'
                  }"
                  class="text-sm px-2 pb-1 transition-colors duration-200"
                >
                  Semua
                </button>
                <button
                  @click.stop="changeFilter('UNREAD')"
                  :class="{
                    // Perbaikan 1: Menggunakan text-teal-700
                    'font-bold text-teal-700 border-b-2 border-teal-700': statusFilter === 'UNREAD',
                    'text-neutral-500 hover:text-neutral-700': statusFilter !== 'UNREAD'
                  }"
                  class="text-sm px-2 pb-1 transition-colors duration-200"
                >
                  Belum Dibaca ({{ unreadNotifications > 10 ? '10+' : unreadNotifications }})
                </button>
                <button
                  @click.stop="changeFilter('READ')"
                  :class="{
                    // Perbaikan 1: Menggunakan text-teal-700
                    'font-bold text-teal-700 border-b-2 border-teal-700': statusFilter === 'READ',
                    'text-neutral-500 hover:text-neutral-700': statusFilter !== 'READ'
                  }"
                  class="text-sm px-2 pb-1 transition-colors duration-200"
                >
                  Sudah Dibaca
                </button>
              </div>

              <div class="max-h-80 overflow-y-auto">
                <div v-if="notifications.length === 0" class="text-center justify-center text-sm text-neutral-500 p-4">
                  Notification is Empty
                </div>
                <div v-else>
                  <div
                    v-for="notification in notifications"
                    :key="notification.notificationId"
                    class="relative p-3 border-b border-neutral-100 cursor-pointer transition-colors duration-150"
                    @click.stop="handleNotificationClick(notification)"
                  >
                    <div class="flex items-start gap-3">
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-semibold text-neutral-800 text-left line-clamp-2 pr-12" :class="{ 'font-bold': !notification.read }">
                          {{ notification.title }}
                        </p>
                        <p class="text-xs text-neutral-600 mt-0.5 text-left line-clamp-3">{{ notification.message }}</p>
                        <p class="text-xs text-neutral-400 mt-1 text-left">
                          {{ formatDateNotification(notification.createdAt) || 'No date available' }}
                        </p>
                      </div>
                    </div>

                    <div v-if="!notification.read" class="absolute top-2 right-2 bg-green-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full shadow-md">
                      Unread
                    </div>
                  </div>

                  <div v-if="hasMoreNotifications" class="text-center p-3">
                    <button @click.stop="loadMore" :disabled="isLoadingMore" class="text-yellow-600 text-sm hover:text-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed">
                      {{ isLoadingMore ? 'Memuat...' : 'Muat Lebih Banyak' }}
                    </button>
                  </div>
                </div>
              </div>

              <div v-if="notifications.length > 0 && unreadNotifications > 0" class="p-3 border-t border-neutral-200 text-center sticky bottom-0 bg-white z-20">
                <span class="inline-block text-yellow-600 text-sm cursor-pointer hover:text-yellow-700 font-medium" @click="markAllAsReadAndClose">
                  Tandai semua sudah dibaca
                </span>
              </div>
            </div>
          </div>
        </div>
        <!-- End Notifications -->
        <ToggleLanguage />
        <div class="h-10 w-10 bg-cover rounded-full" :style="`background-image: url(${user.profile_image})`" v-if="user.profile_image"></div>
        <div class="h-10 w-10 bg-neutral-700 rounded-full flex justify-center items-center" v-else>
          <p :class="`font-bold text-lg text-white ${user?.name !== '' && forInitial(user?.name)?.length > 1 ? '' : 'text-xl'}`">
            {{ user?.name !== '' ? forInitial(user?.name) : 'A' }}
          </p>
        </div>
        <div class="relative text-left pr-10 cursor-pointer user-item">
          <div class="flex items-center gap-2">
            <p class="text-neutral-700 text-lg font-bold leading-tight">{{ user.name }}</p>
            <span v-if="user.is_upgraded" class="vip-badge">
              VIP
            </span>
          </div>
          <p class="text-sm text-neutral-500">{{ user.group }}</p>
          <span class="inline-block absolute top-1/2 leading-0 transform -translate-y-1/2 right-0">
            <Chevron width="16" height="16" direction="down" color="#101840" />
          </span>
          <div class="header__nav__menu-item-submenu invisible opacity-0 ease-linear transition-all duration-300 pt-6 left-2/4 top-full transform -translate-x-2/4 absolute cursor-default z-10">
            <div class="user-menu bg-white shadow-xl border border-grey-darker rounded relative">
              <div class="relative z-10 rounded bg-white p-4 text-black">
                <div class="mb-2">
                  <span class="inline-block text-yellow text-sm cursor-pointer" @click="toggleUserSidebar('PROFILE')">Lihat Profil</span>
                </div>

                <div class="mb-2">
                  <span class="inline-block text-yellow text-sm cursor-pointer" @click="toggleUserSidebar('PARTNER_PROFILE')">Lengkapi Profil Mitra</span>
                </div>

                <div class="mb-2">
                  <span class="inline-block text-yellow text-sm cursor-pointer" @click="toggleUserSidebar('PASSWORD')">Ubah Password</span>
                </div>
                <div class="mb-2">
                  <span class="inline-block text-yellow text-sm cursor-pointer" @click="toggleUserSidebar('ADDRESS')">Pengaturan Alamat</span>
                </div>
                <div class="">
                  <span class="cursor-pointer text-yellow text-sm" @click="doLogout()">Logout</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { formatDateTime } from '@/util'

export default {
  components: {
    Chevron: () => import(/* webpackChunkName: "Icons" */ '@/components/Icons/Chevron'),
    Hamburger: () => import(/* webpackChunkName: "Icons" */ '@/components/Icons/Hamburger'),
    ToggleLanguage: () => import(/* webpackChunkName: "ToggleLanguage" */ '@/components/ToggleLanguage/ToggleLanguage'),
    Bell: () => import(/* webpackChunkName: "Icons" */ '@/components/Icons/Bell')
  },
  data() {
    return {
      showNotifications: false,
      isLoadingNotifications: true,
      isLoadingMore: false
    }
  },
  computed: {
    ...mapGetters('auth', ['userData']),
    ...mapGetters('common', ['expandedMenu', 'isMobile', 'visibleProfileScreen']),
    ...mapGetters('notifications', ['unreadNotifications', 'notifications', 'hasMoreNotifications', 'statusFilter']),
    user() {
      return this.userData?.user
    },
    currentLang() {
      return this.$i18n.locale
    }
  },
  methods: {
    ...mapActions('auth', ['logout']),
    ...mapActions('common', ['showLoading', 'hideLoading', 'expandMenu', 'toggleProfileScreen']),
    ...mapActions('notifications', ['fetchUnreadNotifications', 'fetchNotifications', 'markAsRead', 'markAllAsRead', 'loadMoreNotifications', 'setNotificationFilter']),
    formatDateNotification(date) {
      return formatDateTime(date)
    },
    toggleUserSidebar(state = '') {
      this.toggleProfileScreen({
        options: {
          value: !this.visibleProfileScreen,
          activeComponent: state
        }
      })
    },
    forInitial: function(name) {
      const arr = name?.split(' ')
      if (arr) {
        let newArr = []
        newArr.push(arr[0].slice(0, 1).toUpperCase())
        if (arr.length > 1) newArr.push(arr[1].slice(0, 1).toUpperCase())
        return newArr.join('')
      }
      return
    },
    doLogout() {
      this.showLoading()
      this.logout().then(res => {
        this.hideLoading()
        this.$router.push('/login')
      })
    },
    toggleMenu() {
      const state = this.expandedMenu
      this.expandMenu(!state)
    },
    toggleLanguage() {
      this.$i18n.locale = this.$i18n.locale === 'id' ? 'en' : 'id'
    },
    async toggleNotifications() {
      this.showNotifications = !this.showNotifications
      if (this.showNotifications) {
        this.isLoadingNotifications = true
        // Pastikan selalu memuat halaman 1 saat pertama kali dibuka dengan filter yang aktif
        await this.fetchNotifications({ page: 1, status: this.statusFilter })
        this.isLoadingNotifications = false
      }
    },
    async loadMore() {
      if (this.hasMoreNotifications && !this.isLoadingMore) {
        this.isLoadingMore = true
        await this.loadMoreNotifications()
        this.isLoadingMore = false
      }
    },
    async changeFilter(status) {
      if (this.statusFilter === status) return
      this.isLoadingNotifications = true
      // Gunakan action yang mereset halaman dan melakukan fetch
      await this.setNotificationFilter(status)
      this.isLoadingNotifications = false
    },
    async handleNotificationClick(notification) {
      this.showNotifications = false
      if (!notification.read) {
        await this.markAsRead(notification.notificationId)
      }

      if (notification.redirectType == 'order-detail') {
        this.$router.push(`/order/detail/${notification.dynamicId}`)
      } else if (notification.redirectType == 'draft-order') {
        this.$router.push(`/order/step/shipping?draft=${notification.dynamicId}`)
      } else {
        this.$router.push(`/${notification.redirectType}/${notification.dynamicId}`)
      }
    },
    markAllAsReadAndClose() {
      this.markAllAsRead()
      this.showNotifications = false
    }
  },
  async mounted() {
    await this.fetchUnreadNotifications()
    this.isLoadingNotifications = false
  },
  created() {
    document.addEventListener('click', this.handleOutsideClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick)
  },
  handleOutsideClick(event) {
    const notificationContainer = this.$el.querySelector('.relative.cursor-pointer.mr-5')
    if (this.showNotifications && notificationContainer && !notificationContainer.contains(event.target)) {
      this.showNotifications = false
    }
  }
}
</script>

<style lang="scss" scoped>
.vip-badge {
  display: inline-block;
  position: relative;
  padding: 0.25rem 0.5rem; /* px-2 py-1 */
  border-radius: 0.375rem; /* rounded-md */
  font-weight: 700;
  font-size: 1.125rem; /* text-lg */
  line-height: 1;
  color: #fff;
  overflow: hidden;

  /* gold base gradient */
  background: linear-gradient(90deg, #b8860b 0%, #ffd700 40%, #ffdf6b 60%, #b8860b 100%);

  /* depth/shadow */
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.22), inset 0 -3px 0 rgba(0, 0, 0, 0.12);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* white glossy gradient overlay (top highlight) */
.vip-badge::before {
  content: '';
  position: absolute;
  left: 0;
  top: -6%;
  width: 100%;
  height: 55%;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.45) 30%, rgba(255, 255, 255, 0) 100%);
  transform: translateZ(0);
}
.user-item:hover .header__nav__menu-item-submenu {
  visibility: visible;
  opacity: 1;
  padding-top: 15px;
}

.user-item .header__nav__menu-item-submenu {
  left: auto;
  right: 0;
  transform: none;
}

.user-menu {
  width: 180px;

  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    top: 0;
    box-sizing: border-box;
    border: 6px solid black;
    border-color: transparent transparent #fff #fff;
    transform-origin: 0 0;
    transform: rotate(-225deg);
    box-shadow: 0px 0px 2px 0px rgba(0, 0, 0, 0.5);
    z-index: 1;
    left: auto;
    margin-left: 0;
    right: 0;
    margin-right: 20px;
  }
}

.user-sidebar {
  position: fixed;
  width: 384px;
  height: 100%;
  z-index: 100;
  top: 0;
  right: 0;
  background-color: #fff;
  text-align: left;
  transition: all ease-in-out 300ms;
  overflow: auto;

  &.close {
    transform: translateX(100%);
  }

  &.open {
    transform: translateX(0%);
  }
}

.sidebar-backdrop {
  left: 0;
  top: 0;
  position: fixed;
  background-color: rgba(16, 24, 64, 0.5);
  width: 100%;
  height: 100%;
  z-index: 99;
  visibility: hidden;
  opacity: 0;
  transition: all ease-in 300ms;

  &.open {
    visibility: visible;
    opacity: 1;
  }
}
</style>
